import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { Icon } from "atoms/icon/Icon";
import DownArrow from "atoms/icon/icons/chevron_down_small.svg";
import UpArrow from "atoms/icon/icons/chevron_up_small.svg";

type DropdownProps = {
  invalid?: boolean;
  disabled?: boolean;
  disableClick?: boolean;
  /** This is both the input label and the placeholder text */
  label?: string;
  value?: string | number;
  options: { id: string; name: string }[];
  textValue?: string;
  setValue: (value: string) => void;
  setTextValue?: (value: string) => void;
};

export const Dropdown = ({
  invalid,
  disabled,
  disableClick,
  label,
  value,
  options,
  textValue,
  setValue,
  setTextValue,
}: DropdownProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => {
    if (disabled) {
      return;
    }

    setShowDropdown((value) => !value);
  };
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  return (
    <div className="relative" ref={ref}>
      {value && (
        <span className="absolute -top-2 left-3 z-10 whitespace-nowrap bg-white px-1 text-xs text-secondary">
          {label}
        </span>
      )}
      <div
        className={cx(
          "text-body flex justify-between rounded border bg-white p-3",
          disabled && "opacity-60",
          invalid
            ? "border-red text-red ring-red"
            : showDropdown
              ? "border-brand-color"
              : "border-light-grey",
          textValue ? "text-primary" : "text-secondary",
        )}
        onClick={toggleDropdown}
      >
        {textValue || label}{" "}
        {!disabled && (
          <Icon
            svg={showDropdown ? UpArrow : DownArrow}
            color={invalid ? "red" : "primaryText"}
            size="xsmall"
          />
        )}
      </div>
      {!disabled && showDropdown && (
        <div className="absolute z-40 w-full border bg-white shadow">
          <ul className="max-h-[150px] overflow-scroll">
            {options.map(({ id, name }) => (
              <li
                key={id}
                className="p-3 py-2 text-body1 hover:bg-light-grey"
                onClick={() => {
                  if (disabled || disableClick) {
                    return false;
                  }

                  setValue(id);
                  setTextValue?.(name);
                  toggleDropdown();
                }}
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
